import type { EChartsOption } from 'echarts';
import type { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../../contexts/ThemeContext';

import DashboardEChart from './DashboardEChart';
import { chartTooltipOption, getDashboardChartColors } from './dashboardChartTheme';

function trendLabels(count: number, t: TFunction): string[] {
  if (count === 2) {
    return [
      t('dashboard.overview.trendStart', 'Start'),
      t('dashboard.overview.trendNow', 'Now'),
    ];
  }
  if (count === 3) {
    return [
      t('dashboard.overview.trendWeek1', 'Week 1'),
      t('dashboard.overview.trendWeek4', 'Week 4'),
      t('dashboard.overview.trendNow', 'Now'),
    ];
  }
  if (count >= 4) {
    return [
      t('dashboard.overview.trendWeek1', 'Week 1'),
      t('dashboard.overview.trendWeek4', 'Week 4'),
      t('dashboard.overview.trendWeek9', 'Week 9'),
      t('dashboard.overview.trendWeek12', 'Week 12'),
    ].slice(0, count);
  }
  return Array.from({ length: count }, (_, i) =>
    t('dashboard.overview.trendPoint', { n: i + 1, defaultValue: `P${i + 1}` }),
  );
}

export interface DashboardAreaTrendChartProps {
  points: number[];
  height?: number;
  className?: string;
}

const DashboardAreaTrendChart: React.FC<DashboardAreaTrendChartProps> = ({
  points,
  height = 140,
  className,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const option = useMemo((): EChartsOption => {
    const colors = getDashboardChartColors();
    const labels = trendLabels(points.length, t);

    return {
      grid: { left: 8, right: 12, top: 16, bottom: 4, containLabel: true },
      tooltip: chartTooltipOption(theme, {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const row = Array.isArray(params) ? params[0] : params;
          const p = row as { name?: string; value?: number };
          return `${p.name ?? ''}<br/><b>${p.value ?? 0}%</b>`;
        },
      }),
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: colors.textMuted, fontSize: 10, fontWeight: 600 },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        splitNumber: 2,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          lineStyle: { color: colors.border, type: 'dashed', opacity: 0.5 },
        },
      },
      series: [
        {
          type: 'line',
          data: points,
          smooth: true,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: {
            width: 2,
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: colors.chartOpt },
                { offset: 1, color: colors.ok },
              ],
            },
          },
          itemStyle: {
            color: colors.ok,
            borderColor: colors.ok,
            borderWidth: 2,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(52, 211, 153, 0.14)' },
                { offset: 1, color: 'rgba(52, 211, 153, 0)' },
              ],
            },
          },
        },
      ],
      animationDuration: 800,
      animationEasing: 'cubicOut',
    };
  }, [points, t, theme]);

  return <DashboardEChart option={option} height={height} className={className} />;
};

export default DashboardAreaTrendChart;
